<template>
  <div>
    <!-- 面包屑导航 -->
    <el-breadcrumb separator="/">
      <el-breadcrumb-item :to="{ path: '/home' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>运营部</el-breadcrumb-item>
      <el-breadcrumb-item :to="{ path: '/dudao/choujian' }">筹建督导工作台</el-breadcrumb-item>
      <el-breadcrumb-item>筹建操作</el-breadcrumb-item>
    </el-breadcrumb>

    <!-- 页面内容 -->
    <el-card class="box-card" style="margin-top: 20px;">
      <el-page-header @back="goBack" content="筹建详情">
      </el-page-header>

      <el-divider></el-divider>

      <!-- 加载状态 -->
      <div v-if="pageLoading" v-loading="true" style="min-height: 400px;"></div>

      <!-- 详情内容 -->
      <div v-else-if="currentDetail" class="detail-page-container">
        <el-row :gutter="20">
          <el-col :span="12" :offset="0">
            <!-- ===== 顶部项目信息区 ===== -->
            <div class="detail-header">
              <div class="detail-header-left">
                <div class="detail-project-name">
                  <i class="el-icon-s-shop"></i>
                  {{ currentDetail.instance.projectName || '-' }}
                  <el-tooltip content="刷新详情" placement="top">
                    <i class="el-icon-refresh detail-refresh-icon" @click="refreshDetail"></i>
                  </el-tooltip>
                </div>
                <div class="detail-meta-row">
                  <span class="detail-meta-item">
                    <span class="detail-meta-label">筹建督导：</span>
                    <span class="detail-meta-value">{{ getAssigneeName(currentDetail.instance.assignees, 'supervisor') }}</span>
                  </span>
                  <span class="detail-meta-item">
                    <span class="detail-meta-label">接收时间：</span>
                    <span class="detail-meta-value">{{ currentDetail.instance.startedAt | date }}</span>
                  </span>
                </div>
                <div class="detail-address" v-if="detailAddress">
                  <i class="el-icon-location-outline"></i>
                  <span class="detail-address-text">{{ detailAddress }}</span>
                </div>
              </div>
              <div class="detail-progress-card">
                <div class="detail-progress-label">整体进度</div>
                <div class="detail-progress-value">
                  <span class="detail-progress-number">{{ currentDetail.instance.progress?.percentage || 0 }}</span>
                  <span class="detail-progress-unit">%</span>
                </div>
                <el-progress
                  :percentage="currentDetail.instance.progress?.percentage || 0"
                  :color="getProgressColor(currentDetail.instance.progress?.percentage)"
                  :stroke-width="8"
                  :show-text="false">
                </el-progress>
                <div class="detail-progress-info">
                  {{ currentDetail.instance.progress?.completed || 0 }}/{{ currentDetail.instance.progress?.total || 0 }} 节点完成
                </div>
              </div>
            </div>

            <!-- 首批物料备忘（招商部录入，详情接口自动下发） -->
            <div class="detail-memo-card" v-if="hasMemo(projectMemo)">
              <div class="detail-memo-header">
                <span class="detail-memo-title"><i class="el-icon-warning-outline"></i> 首批物料备忘</span>
                <span class="detail-memo-source">招商部提供</span>
              </div>
              <div class="detail-memo-text" v-if="projectMemo.text">{{ projectMemo.text }}</div>
              <div class="detail-memo-images" v-if="projectMemo.images && projectMemo.images.length">
                <el-image
                  v-for="(img, idx) in projectMemo.images"
                  :key="idx"
                  :src="getMemoImg(img)"
                  :preview-src-list="projectMemo.images.map(i => getMemoImg(i))"
                  style="width: 40px; height: 40px; margin-right: 6px; cursor: pointer; border-radius: 4px;"
                  fit="cover">
                </el-image>
              </div>
            </div>

            <!-- ===== 阶段进度区域 ===== -->
            <div class="detail-section">
              <div class="detail-section-title">阶段进度</div>
              <div class="stage-cards">
                <div
                  v-for="stage in stageList"
                  :key="stage.code"
                  class="stage-card"
                  :class="getStageClass(stage)">
                  <div class="stage-name">{{ stage.name }}</div>
                  <div class="stage-status">{{ getStageStatusText(stage) }}</div>
                  <el-progress
                    :percentage="getStagePercentage(stage)"
                    :color="getStageColor(stage)"
                    :stroke-width="4"
                    :show-text="false">
                  </el-progress>
                </div>
              </div>
            </div>

            <!-- ===== 资料收集区域 ===== -->
            <div class="detail-section" v-if="dataCollectNode && dataCollectNode.status === 'COMPLETED'">
              <div class="detail-section-header">
                <span class="detail-section-title">资料收集</span>
                <el-button
                  v-if="canEditImages"
                  type="text"
                  size="small"
                  icon="el-icon-edit"
                  @click="handleEditDataCollect">
                  编辑
                </el-button>
              </div>
              <el-descriptions :column="3" size="small" border>
                <el-descriptions-item label="完成时间">{{ dataCollectNode.completedAt | date }}</el-descriptions-item>
                <el-descriptions-item label="门店地址" :span="2">{{ dataCollectNode.data?.storeAddress || '-' }}</el-descriptions-item>
                <el-descriptions-item label="法人姓名">{{ dataCollectNode.data?.legalPersonName || '-' }}</el-descriptions-item>
                <el-descriptions-item label="法人身份证">{{ dataCollectNode.data?.legalPersonId || '-' }}</el-descriptions-item>
                <el-descriptions-item label="店铺绑定号码">{{ dataCollectNode.data?.storePhone || '-' }}</el-descriptions-item>
                <el-descriptions-item label="开户行">{{ dataCollectNode.data?.bankName || '-' }}</el-descriptions-item>
                <el-descriptions-item label="银行卡号">{{ dataCollectNode.data?.bankAccount || '-' }}</el-descriptions-item>
                <el-descriptions-item label="银行预留号码">{{ dataCollectNode.data?.bankPhone || '-' }}</el-descriptions-item>
                <el-descriptions-item label="备注">{{ dataCollectNode.data?.remark || '暂无' }}</el-descriptions-item>
                <el-descriptions-item label="图片" :span="2">
                  <el-image
                    v-for="(img, idx) in (dataCollectNode.data?.images || [])"
                    :key="idx"
                    :src="base + img"
                    :preview-src-list="(dataCollectNode.data?.images || []).map(i => base + i)"
                    style="width: 30px; height: 30px; margin-right: 5px; cursor: pointer;"
                    fit="cover">
                  </el-image>
                  <span v-if="!dataCollectNode.data?.images?.length">-</span>
                </el-descriptions-item>
              </el-descriptions>
            </div>

            <!-- ===== 平台账号区域 ===== -->
            <div class="detail-section" v-if="accountNodes.create && accountNodes.create.status === 'COMPLETED'">
              <div class="detail-section-title">平台账号</div>
              <el-descriptions :column="3" size="small" border>
                <el-descriptions-item label="建店专员">{{ accountNodes.assign?.data?.employeeName || accountNodes.create?.assigneeName || '-' }}</el-descriptions-item>
                <el-descriptions-item label="通知时间">{{ accountNodes.notify?.completedAt | date }}</el-descriptions-item>
                <el-descriptions-item label="创建完成">{{ accountNodes.create?.completedAt | date }}</el-descriptions-item>
                <el-descriptions-item label="美团店铺名称">{{ accountNodes.create?.data?.meituanName || '-' }}</el-descriptions-item>
                <el-descriptions-item label="美团店铺ID">{{ accountNodes.create?.data?.meituanId || '-' }}</el-descriptions-item>
                <el-descriptions-item label="">-</el-descriptions-item>
                <el-descriptions-item label="淘宝店铺名称">{{ accountNodes.create?.data?.taobaoName || '-' }}</el-descriptions-item>
                <el-descriptions-item label="淘宝店铺ID">{{ accountNodes.create?.data?.taobaoId || '-' }}</el-descriptions-item>
                <el-descriptions-item label="">-</el-descriptions-item>
                <el-descriptions-item label="京东店铺名称">{{ accountNodes.create?.data?.jdName || '-' }}</el-descriptions-item>
                <el-descriptions-item label="京东店铺ID">{{ accountNodes.create?.data?.jdId || '-' }}</el-descriptions-item>
                <el-descriptions-item label="">-</el-descriptions-item>
              </el-descriptions>
            </div>

            <!-- ===== 产品审核区域 ===== -->
            <div class="detail-section" v-if="productReviewNode && productReviewNode.loopRecords && productReviewNode.loopRecords.length > 0">
              <div class="detail-section-title">产品审核</div>
              <el-table :data="productReviewNode.loopRecords" border size="small">
                <el-table-column type="index" label="序号" width="60"></el-table-column>
                <el-table-column label="审核时间" width="180">
                  <template slot-scope="scope">{{ scope.row.date | date }}</template>
                </el-table-column>
                <el-table-column label="图片" min-width="200">
                  <template slot-scope="scope">
                    <span v-if="scope.row.images && scope.row.images.length > 0">
                      <el-image
                        v-for="(img, idx) in scope.row.images"
                        :key="idx"
                        :src="base + img"
                        :preview-src-list="scope.row.images.map(i => base + i)"
                        style="width: 30px; height: 30px; margin-right: 5px; cursor: pointer;"
                        fit="cover">
                      </el-image>
                    </span>
                    <span v-else>--</span>
                  </template>
                </el-table-column>
                <el-table-column label="备注" width="150">
                  <template slot-scope="scope">{{ scope.row.remark || '--' }}</template>
                </el-table-column>
                <el-table-column label="操作" width="80">
                  <template slot-scope="scope">
                    <el-button
                      v-if="canEditImages"
                      type="text"
                      size="mini"
                      @click.stop="handleEditProductReview(scope.row, scope.$index)">
                      编辑
                    </el-button>
                    <span v-else>--</span>
                  </template>
                </el-table-column>
              </el-table>
            </div>

          </el-col>

          <el-col :span="12" :offset="0">
            <!-- ===== 节点详情表格 ===== -->
            <div class="detail-section">
              <div class="detail-section-header">
                <span class="detail-section-title">节点详情</span>
                <div class="detail-section-toggle">
                  <el-switch
                    v-model="showCompletedNodes"
                    active-text="显示已完成"
                    inactive-text="仅待处理"
                    size="small">
                  </el-switch>
                </div>
              </div>
              <el-table
                :data="filteredNodes"
                size="small"
                border
                stripe
                @row-click="handleNodeRowClick">
                <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
                <el-table-column label="节点名称" min-width="150">
                  <template slot-scope="scope">
                    {{ scope.row.name }}
                    <el-tag
                      v-if="scope.row.data && scope.row.data.transferReopen && scope.row.status !== 'COMPLETED'"
                      size="mini"
                      type="warning"
                      style="margin-left:5px;">
                      转让需重开
                    </el-tag>
                    <span v-if="scope.row.code === 'STORE_OPENING' && scope.row.status === 'COMPLETED' && scope.row.data?.openingAt && getOpenedDays(scope.row.data.openingAt) >= 0" class="node-days-tag">
                      （已开业{{ getOpenedDays(scope.row.data.openingAt) }}天）
                    </span>
                    <span v-if="scope.row.code === 'STORE_OPENING' && scope.row.status === 'COMPLETED' && scope.row.data?.openingAt && getOpenedDays(scope.row.data.openingAt) < 0" class="node-days-tag" style="color: #409EFF;">
                      （{{ -getOpenedDays(scope.row.data.openingAt) }}天后开业）
                    </span>
                  </template>
                </el-table-column>
                <el-table-column label="负责人" width="100">
                  <template slot-scope="scope">
                    {{ scope.row.assigneeName || '-' }}
                  </template>
                </el-table-column>
                <el-table-column label="状态" width="100" align="center">
                  <template slot-scope="scope">
                    <el-tag
                      :type="getNodeStatusType(scope.row.status)"
                      size="mini"
                      :effect="getNodeStatusType(scope.row.status)==='danger'?'dark':'light'">
                      {{ getNodeStatusText(scope.row) }}
                    </el-tag>
                  </template>
                </el-table-column>
                <el-table-column label="完成时间" width="110">
                  <template slot-scope="scope">
                    <span v-if="scope.row.status === 'COMPLETED' || scope.row.status === 'SKIPPED'">
                      {{ scope.row.completedAt | date }}
                    </span>
                    <span v-else class="text-muted">-</span>
                  </template>
                </el-table-column>
                <el-table-column label="操作" width="140" align="center">
                  <template slot-scope="scope">
                    <!-- 已完成且非决策节点：显示查看和编辑按钮 -->
                    <template v-if="scope.row.status === 'COMPLETED' && scope.row.type !== 'BRANCH'">
                      <el-button
                        type="text"
                        size="small"
                        @click.stop="handleNodeDetail(scope.row)">
                        查看
                      </el-button>
                      <el-button
                        v-if="canEditNodeImages(scope.row) || canEditNodeAssignment(scope.row)"
                        type="text"
                        size="small"
                        @click.stop="handleEditNodeClick(scope.row)">
                        编辑
                      </el-button>
                    </template>
                    <!-- 已完成的决策节点：管理员、运营总监、督导主管可修改 -->
                    <template v-else-if="scope.row.status === 'COMPLETED' && scope.row.type === 'BRANCH'">
                      <el-button
                        v-if="canModifyDecision"
                        type="text"
                        size="small"
                        @click.stop="handleModifyDecision(scope.row)">
                        修改决策
                      </el-button>
                      <span v-else class="text-muted">-</span>
                    </template>
                    <!-- 已跳过：显示 "-" -->
                    <span v-else-if="scope.row.status === 'SKIPPED'" class="text-muted">-</span>
                    <!-- 进行中且是负责人：显示操作按钮 -->
                    <el-button
                      v-else-if="canExecuteNode(scope.row) && isNodeAssignee(scope.row)"
                      type="text"
                      size="small"
                      @click.stop="handleNodeExecute(scope.row)">
                      {{ getNodeActionText(scope.row) }}
                    </el-button>
                    <!-- 进行中但不是负责人：显示等待完成 -->
                    <span v-else-if="canExecuteNode(scope.row) && !isNodeAssignee(scope.row)" class="text-waiting">
                      等待完成
                    </span>
                    <!-- 其他情况 -->
                    <span v-else class="text-muted">-</span>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </el-col>
        </el-row>

        <!-- ===== 底部操作区域 ===== -->
        <div class="detail-actions" v-if="currentActiveNode">
          <div class="detail-actions-left">
            <span class="detail-actions-label">当前节点：</span>
            <span class="detail-actions-node">{{ currentActiveNode.name }}</span>
          </div>
          <div class="detail-actions-right">
            <el-button size="small" @click="showNodeHistory">查看历史</el-button>
            <el-button type="danger" size="small" @click="executeCurrentNode">提交完成</el-button>
          </div>
        </div>
      </div>

      <!-- 无数据提示 -->
      <el-empty v-else description="未找到筹建详情数据"></el-empty>
    </el-card>

    <!-- 通用节点操作弹窗 -->
    <el-dialog
      :title="getNodeActionTitle()"
      :visible.sync="nodeActionDialogVisible"
      :width="needsMap ? '80%' : '600px'"
      :top="needsMap ? '5vh' : '10vh'"
      :close-on-click-modal="false"
      @opened="onNodeDialogOpened"
      @closed="onNodeDialogClosed">
      <el-row :gutter="20">
        <!-- 左侧表单区域 -->
        <el-col :span="needsMap ? 14 : 24">

            <!-- ========== 转让重开节点提示 ========== -->
            <el-alert
              v-if="currentActionNode && currentActionNode.data && currentActionNode.data.transferReopen"
              type="warning"
              :closable="false"
              show-icon
              style="margin-bottom: 15px;"
              title="项目已转让，原资料已清空"
              description="请按受让人重新填写/上传资料后提交；该节点未完成前不能完成门店开业/切换运营中。"
            />

            <!-- ========== 法人检测状态提示（仅营业执照节点显示） ========== -->
            <div v-if="currentActionNode && currentActionNode.code === 'BUSINESS_LICENSE'" class="legal-check-tip">
              <!-- 未检测 -->
              <el-alert v-if="legalCheckResult === null" type="info" :closable="false" show-icon>
                请先上传营业执照图片，然后点击"法人检测"按钮
              </el-alert>
              <!-- 检测通过 -->
              <el-alert v-if="legalCheckResult === true" type="success" :closable="false" show-icon>
                法人身份检测通过，可以提交
              </el-alert>
              <!-- 检测不通过且图片数不足 -->
              <el-alert v-if="legalCheckResult === false && (nodeActionForm.images?.length || 0) <= 1" type="error" :closable="false" show-icon>
                法人与签约人不一致，请上传承诺书后再提交（当前图片数：{{ nodeActionForm.images?.length || 0 }}）
              </el-alert>
              <!-- 检测不通过但图片数已满足 -->
              <el-alert v-if="legalCheckResult === false && (nodeActionForm.images?.length || 0) > 1" type="warning" :closable="false" show-icon>
                法人与签约人不一致，承诺书已上传，可以提交
              </el-alert>
            </div>

            <!-- ========== FEEDBACK 节点审核内容展示区 ========== -->
            <div v-if="feedbackReviewContent" class="feedback-review-content">
              <div class="feedback-review-header">
                <i class="el-icon-document-checked"></i>
                <span>{{ feedbackReviewContent.nodeName }}（{{ feedbackReviewContent.assigneeName || '未知' }} 提交）</span>
              </div>

              <!-- 历史审核反馈记录 -->
              <div v-if="feedbackReviewContent.feedbackRecords.length > 0" class="feedback-history">
                <div class="feedback-history-title">
                  <i class="el-icon-time"></i>
                  <span>历史审核记录（{{ feedbackReviewContent.feedbackRecords.length }} 次修改）</span>
                </div>
                <div
                  v-for="(record, rIdx) in feedbackReviewContent.feedbackRecords"
                  :key="rIdx"
                  class="feedback-history-item">
                  <div class="feedback-history-meta">
                    <span>第 {{ rIdx + 1 }} 次提交</span>
                    <span class="feedback-history-time">{{ record.createdAt | date }}</span>
                  </div>
                  <!-- 历史图片缩略图 -->
                  <div v-if="record.images && record.images.length > 0" class="feedback-history-images">
                    <el-image
                      v-for="(img, imgIdx) in record.images"
                      :key="imgIdx"
                      :src="base + img"
                      :preview-src-list="record.images.map(i => base + i)"
                      style="width: 50px; height: 50px; margin-right: 6px;"
                      fit="cover">
                    </el-image>
                  </div>
                  <!-- 历史修改意见 -->
                  <div v-if="record.feedback" class="feedback-history-opinion">
                    <span class="feedback-label">修改意见：</span>{{ record.feedback }}
                  </div>
                </div>
              </div>

              <!-- 当前提交的设计稿 -->
              <div class="feedback-current-submission">
                <div class="feedback-current-title">
                  <i class="el-icon-picture-outline"></i>
                  <span>当前设计稿</span>
                </div>
                <!-- 设计稿图片 -->
                <div v-if="feedbackReviewContent.images.length > 0" class="feedback-images">
                  <el-image
                    v-for="(img, idx) in feedbackReviewContent.images"
                    :key="idx"
                    :src="base + img"
                    :preview-src-list="feedbackReviewContent.images.map(i => base + i)"
                    style="width: 120px; height: 120px; margin-right: 10px; margin-bottom: 10px;"
                    fit="cover">
                  </el-image>
                </div>
                <div v-else class="feedback-no-images">暂无设计稿</div>
                <!-- 备注 -->
                <div v-if="feedbackReviewContent.remark" class="feedback-remark">
                  <span class="feedback-label">备注：</span>{{ feedbackReviewContent.remark }}
                </div>
              </div>

              <el-divider></el-divider>
            </div>
            <!-- ========== 审核内容展示区结束 ========== -->
            <!-- ========== 实操培训答题情况展示区 ========== -->
            <div v-if="isPracticalTrainingNode && currentExamResult !== null" class="exam-result-section">
              <div class="exam-result-header">
                <i class="el-icon-notebook-2"></i>
                <span>实操测试答题情况</span>
              </div>
              <div v-if="currentExamResult.list.length === 0" class="exam-result-empty">暂无答题记录</div>
              <div v-else ref="examResultCapture">
                <el-tag
                  :type="isExamSatisfied(currentExamResult) ? 'success' : 'danger'"
                  size="medium"
                  style="margin-bottom: 10px;">
                  {{ examResultTagText(currentExamResult) }}
                </el-tag>
                <el-table :data="currentExamResult.list" border size="mini" style="width: 100%;">
                  <el-table-column prop="name" label="姓名"></el-table-column>
                  <el-table-column label="最高分" width="80">
                    <template slot-scope="scope">
                      <span :style="{ color: scope.row.passed ? '#67C23A' : '#F56C6C', fontWeight: 'bold' }">
                        {{ scope.row.score }}
                      </span>
                    </template>
                  </el-table-column>
                  <el-table-column label="是否通过" width="90">
                    <template slot-scope="scope">
                      <el-tag :type="scope.row.passed ? 'success' : 'danger'" size="mini">
                        {{ scope.row.passed ? '通过' : '未通过' }}
                      </el-tag>
                    </template>
                  </el-table-column>
                  <el-table-column label="提交时间" width="150">
                    <template slot-scope="scope">
                      {{ scope.row.submitTime | dateFormat }}
                    </template>
                  </el-table-column>
                </el-table>
              </div>
              <el-divider></el-divider>
            </div>
            <!-- ========== 答题情况展示区结束 ========== -->

            <el-form
            ref="nodeActionFormRef"
            :model="nodeActionForm"
            :rules="nodeActionRules"
            size="small"
            label-width="120px">
            <!-- 人员选择器（仅 employeeSelection 存在时显示） -->
            <el-form-item
              v-if="currentActionNode && currentActionNode.employeeSelection"
              :label="currentActionNode.employeeSelection.roleName"
              required>
              <el-select
                v-model="nodeActionForm.employeeId"
                placeholder="请选择"
                filterable
                style="width: 100%"
                @change="onEmployeeChange">
                <el-option
                  v-for="item in currentEmployeeList"
                  :key="item._id"
                  :label="item.name"
                  :value="item._id">
                </el-option>
              </el-select>
            </el-form-item>

            <!-- 其他 dataFields 字段 -->
            <template v-for="field in filteredDataFields">
              <!-- 图片上传 -->
              <el-form-item
                v-if="field.fieldType === 'IMAGE'"
                :key="field.fieldCode"
                :label="field.fieldName"
                :required="field.required">
                <!-- 实操培训：自动截图按钮 -->
                <div v-if="isPracticalTrainingNode && currentExamResult && currentExamResult.list.length > 0 && field.fieldCode === 'images'" style="margin-bottom: 10px;">
                  <el-button type="success" size="small" icon="el-icon-camera" @click="captureAndUploadExamResult">
                    自动截图答题结果
                  </el-button>
                </div>
                <ImageUpload
                  :ref="'imageUpload_' + field.fieldCode"
                  v-model="nodeActionForm[field.fieldCode]"
                  :return-object="true"
                  :active="nodeActionDialogVisible"
                  :show-tip="true"
                  @preview="handleUploadPreview"
                  @upload-success="(data) => handleNodeUploadSuccess(data, field.fieldCode)"
                />
              </el-form-item>

              <!-- 日期选择 -->
              <el-form-item
                v-else-if="field.fieldType === 'DATE'"
                :key="field.fieldCode"
                :label="field.fieldName"
                :required="field.required">
                <el-date-picker
                  v-model="nodeActionForm[field.fieldCode]"
                  type="datetime"
                  :placeholder="'选择' + field.fieldName"
                  value-format="yyyy-MM-dd HH:mm:ss"
                  style="width: 100%;">
                </el-date-picker>
              </el-form-item>

              <!-- 文件上传 -->
              <el-form-item
                v-else-if="field.fieldType === 'FILE'"
                :key="field.fieldCode"
                :label="field.fieldName"
                :required="field.required">
                <el-upload
                  :action="uploadUrl"
                  :headers="uploadHeaders"
                  :on-success="(res, file, fileList) => handleNodeFileSuccess(res, file, fileList, field.fieldCode)"
                  :on-remove="(file, fileList) => handleNodeFileRemove(file, fileList, field.fieldCode)"
                  :file-list="nodeActionFileList[field.fieldCode] || []">
                  <el-button size="small" type="primary">点击上传</el-button>
                </el-upload>
              </el-form-item>

              <!-- 数字输入 -->
              <el-form-item
                v-else-if="field.fieldType === 'NUMBER'"
                :key="field.fieldCode"
                :label="field.fieldName"
                :required="field.required">
                <el-input-number
                  v-model="nodeActionForm[field.fieldCode]"
                  :precision="6"
                  style="width: 100%;">
                </el-input-number>
              </el-form-item>

              <!-- 决策单选（BRANCH节点） -->
              <el-form-item
                v-else-if="field.fieldType === 'SELECT' && currentActionNode && currentActionNode.type === 'BRANCH'"
                :key="field.fieldCode"
                :label="field.fieldName"
                :required="field.required">
                <el-radio-group v-model="nodeActionForm[field.fieldCode]">
                  <el-radio
                    v-for="opt in field.options"
                    :key="opt.value"
                    :label="opt.value">
                    {{ opt.label }}
                  </el-radio>
                </el-radio-group>
              </el-form-item>

              <!-- 反馈节点 -->
              <el-form-item
                v-else-if="field.fieldType === 'SELECT' && currentActionNode && currentActionNode.type === 'FEEDBACK'"
                :key="field.fieldCode"
                :label="field.fieldName"
                :required="field.required">
                <el-radio-group v-model="nodeActionForm[field.fieldCode]">
                  <el-radio
                    v-for="opt in field.options"
                    :key="opt.value"
                    :label="opt.value">
                    {{ opt.label }}
                  </el-radio>
                </el-radio-group>
              </el-form-item>

              <!-- 地址字段（DATA_COLLECT特殊处理） -->
              <el-form-item
                v-else-if="field.fieldCode === 'storeAddress' && needsMap"
                :key="field.fieldCode"
                :label="field.fieldName"
                :required="field.required">
                <el-row :gutter="10">
                  <el-col :span="14">
                    <el-input v-model="nodeActionForm[field.fieldCode]" :placeholder="'请输入' + field.fieldName"></el-input>
                  </el-col>
                  <el-col :span="6">
                    <el-button type="primary" size="small" @click="autoLocate">自动定位</el-button>
                  </el-col>
                </el-row>
              </el-form-item>

              <!-- 普通文本输入 -->
              <el-form-item
                v-else
                :key="field.fieldCode"
                :label="field.fieldName"
                :required="field.required">
                <el-input
                  v-model="nodeActionForm[field.fieldCode]"
                  :placeholder="'请输入' + field.fieldName"
                  :type="field.fieldCode === 'remark' ? 'textarea' : 'text'"
                  :rows="field.fieldCode === 'remark' ? 3 : 1">
                </el-input>
                <!-- 美团订货注册：供应链编码录入提示 -->
                <div
                  v-if="currentActionNode && currentActionNode.code === 'MEITUAN_ORDER_REGISTER' && (field.fieldCode === 'meituanCode' || field.fieldCode === 'shihengCode')"
                  class="scm-code-tip">
                  {{ field.fieldCode === 'meituanCode' ? '美团管家系统订货机构编号' : '食亨（订货通）客户编码' }}；已预填财务门店映射的最新编码，请核对确认；两个编码至少填一个
                </div>
              </el-form-item>
            </template>
          </el-form>
        </el-col>

        <!-- 右侧地图区域（仅 DATA_COLLECT 节点显示） -->
        <el-col v-if="needsMap" :span="10">
          <div class="map-info" v-if="nodeActionForm.province">
            {{ nodeActionForm.province }}{{ nodeActionForm.city }}{{ nodeActionForm.district }}
            | {{ nodeActionForm.lat?.toFixed(6) }}, {{ nodeActionForm.lng?.toFixed(6) }}
          </div>
          <div id="node-map-container" class="map-container"></div>
        </el-col>
      </el-row>

      <span slot="footer">
        <!-- loop节点的特殊操作 -->
        <template v-if="currentActionNode && currentActionNode.type === 'LOOP'">
          <span class="loop-info">已完成 {{ currentActionNode.loopCount }}/{{ currentActionNode.loopConfig?.minCount || 1 }} 次</span>
          <el-button @click="nodeActionDialogVisible = false">取消</el-button>
          <el-button type="primary" :loading="nodeActionLoading" @click="confirmNodeAction()">添加记录</el-button>
          <el-button type="success" :disabled="!canCompleteLoop" :loading="completeLoopLoading" @click="confirmCompleteLoop">完成节点</el-button>
        </template>
        <template v-else>
          <el-button @click="nodeActionDialogVisible = false">取消</el-button>
          <el-button
            v-if="currentActionNode && currentActionNode.code === 'BUSINESS_LICENSE'"
            type="warning"
            :loading="legalCheckLoading"
            @click="handleLegalPersonCheck">法人检测</el-button>
          <el-button type="primary" :loading="nodeActionLoading" :disabled="!canSubmitBusinessLicense" @click="confirmNodeAction()">确定</el-button>
        </template>
      </span>
    </el-dialog>

    <!-- 节点详情弹窗（只读） -->
    <el-dialog
      title="节点详情"
      :visible.sync="nodeDetailDialogVisible"
      width="500px"
      :close-on-click-modal="false">
      <div v-if="currentDetailNode" class="node-detail-content">
        <el-descriptions :column="1" border size="small">
          <el-descriptions-item label="节点名称">{{ currentDetailNode.name }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="getNodeStatusType(currentDetailNode.status)" size="small">
              {{ getNodeStatusText(currentDetailNode) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="负责人">{{ currentDetailNode.assigneeName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="完成时间">{{ currentDetailNode.completedAt | date }}</el-descriptions-item>
        </el-descriptions>

        <!-- 人员选择节点：显示被分配人信息 -->
        <div v-if="currentDetailNode.employeeSelection" class="node-detail-assignee">
          <div class="node-detail-section-title">分配信息</div>
          <el-descriptions :column="1" border size="small">
            <el-descriptions-item :label="currentDetailNode.employeeSelection.roleName">
              {{ currentDetailNode.data.employeeName || '-' }}
            </el-descriptions-item>
            <!-- 约定时间：仅当存在 scheduledAt 字段时显示 -->
            <el-descriptions-item v-if="currentDetailNode.data.scheduledAt" label="约定时间">
              {{ currentDetailNode.data.scheduledAt | date }}
            </el-descriptions-item>
          </el-descriptions>
        </div>

        <!-- 动态显示 dataFields 中的数据（排除 employeeId、employeeName、scheduledAt） -->
        <div v-if="currentDetailNode.dataFields && currentDetailNode.dataFields.length > 0" class="node-detail-data">
          <template v-for="field in currentDetailNode.dataFields">
            <!-- 跳过隐藏字段、id字段、以及人员选择相关字段 -->
            <template v-if="!field.hidden && !field.fieldCode.endsWith('Id') && field.fieldCode !== 'employeeName' && field.fieldCode !== 'scheduledAt'">
              <div :key="field.fieldCode" class="node-detail-item">
                <div class="node-detail-label">{{ field.fieldName }}</div>
                <!-- 图片字段 -->
                <div v-if="field.fieldType === 'IMAGE'" class="node-detail-value">
                  <el-image
                    v-for="(img, idx) in (currentDetailNode.data[field.fieldCode] || [])"
                    :key="idx"
                    :src="base + img"
                    :preview-src-list="(currentDetailNode.data[field.fieldCode] || []).map(i => base + i)"
                    style="width: 60px; height: 60px; margin-right: 8px;"
                    fit="cover">
                  </el-image>
                  <span v-if="!currentDetailNode.data[field.fieldCode]?.length">-</span>
                </div>
                <!-- 日期字段 -->
                <div v-else-if="field.fieldType === 'DATE'" class="node-detail-value">
                  {{ currentDetailNode.data[field.fieldCode] | date }}
                </div>
                <!-- 其他字段（美团订货注册编码字段经 getNodeDataDisplayValue 取映射最新值） -->
                <div v-else class="node-detail-value">
                  {{ getNodeDataDisplayValue(field) || '-' }}
                </div>
              </div>
            </template>
          </template>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="nodeDetailDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 节点图片编辑弹窗 -->
    <el-dialog
      title="编辑节点图片"
      :visible.sync="imageEditDialogVisible"
      width="600px"
      :close-on-click-modal="false"
      @closed="onImageEditDialogClosed">
      <div v-if="currentEditNode">
        <div class="image-edit-header">
          <span class="image-edit-node-name">{{ currentEditNode.name }}</span>
          <span class="image-edit-status">
            <el-tag :type="getNodeStatusType(currentEditNode.status)" size="small">
              {{ getNodeStatusText(currentEditNode) }}
            </el-tag>
          </span>
        </div>
        <el-divider></el-divider>
        <el-form size="small">
          <el-form-item label="图片">
            <ImageUpload
              ref="imageEditUpload"
              v-model="imageEditForm.images"
              :return-object="true"
              :active="imageEditDialogVisible"
              :show-tip="true"
              @preview="handleImageEditPreview"
            />
          </el-form-item>
          <el-form-item label="备注">
            <el-input
              type="textarea"
              :rows="2"
              placeholder="请输入备注"
              v-model="imageEditForm.remark">
            </el-input>
          </el-form-item>
          <!-- 美团订货注册：供应链编码编辑（回显财务映射表最新值；至少填一个） -->
          <template v-if="currentEditNode.code === 'MEITUAN_ORDER_REGISTER'">
            <el-form-item label="美团管家编码">
              <el-input
                v-model="imageEditForm.meituanCode"
                placeholder="美团管家系统订货机构编号">
              </el-input>
            </el-form-item>
            <el-form-item label="食亨编码">
              <el-input
                v-model="imageEditForm.shihengCode"
                placeholder="食亨（订货通）客户编码">
              </el-input>
            </el-form-item>
            <el-form-item label-width="0">
              <div class="scm-code-tip">已回显财务门店映射的最新编码；两个编码至少填一个；清空单侧=改码（映射不会同步清除，清码请联系财务）</div>
            </el-form-item>
          </template>
        </el-form>
      </div>
      <span slot="footer">
        <el-button @click="imageEditDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="imageEditLoading" @click="confirmImageEdit">保存</el-button>
      </span>
    </el-dialog>

    <!-- 产品审核图片编辑弹窗 -->
    <el-dialog
      title="编辑产品审核记录"
      :visible.sync="productReviewEditDialogVisible"
      width="600px"
      :close-on-click-modal="false"
      @closed="onProductReviewEditDialogClosed">
      <div v-if="currentEditProductReview">
        <div class="image-edit-header">
          <span class="image-edit-node-name">产品审核</span>
          <span class="image-edit-status">
            第 {{ currentEditProductReview.recordIndex + 1 }} 次审核
          </span>
          <span class="image-edit-time">
            {{ currentEditProductReview.record.date | date }}
          </span>
        </div>
        <el-divider></el-divider>
        <el-form size="small">
          <el-form-item label="图片">
            <ImageUpload
              ref="productReviewEditUpload"
              v-model="productReviewEditForm.images"
              :return-object="true"
              :active="productReviewEditDialogVisible"
              :show-tip="true"
              @preview="handleProductReviewEditPreview"
            />
          </el-form-item>
          <el-form-item label="备注">
            <el-input
              type="textarea"
              :rows="2"
              placeholder="请输入备注"
              v-model="productReviewEditForm.remark">
            </el-input>
          </el-form-item>
        </el-form>
      </div>
      <span slot="footer">
        <el-button @click="productReviewEditDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="productReviewEditLoading" @click="confirmProductReviewEdit">保存</el-button>
      </span>
    </el-dialog>

    <!-- 数据编辑弹窗（人员重新选择 / 日期修改） -->
    <el-dialog
      title="编辑节点数据"
      :visible.sync="dataEditDialogVisible"
      width="500px"
      :close-on-click-modal="false"
      @closed="onDataEditDialogClosed">
      <div v-if="currentDataEditNode">
        <div class="image-edit-header">
          <span class="image-edit-node-name">{{ currentDataEditNode.name }}</span>
          <span class="image-edit-status">
            <el-tag :type="getNodeStatusType(currentDataEditNode.status)" size="small">
              {{ getNodeStatusText(currentDataEditNode) }}
            </el-tag>
          </span>
        </div>
        <el-divider></el-divider>
        <el-form size="small" label-width="120px">
          <!-- 人员选择（employeeSelection 节点） -->
          <el-form-item
            v-if="currentDataEditNode.employeeSelection"
            :label="currentDataEditNode.employeeSelection.roleName"
            required>
            <el-select
              v-model="dataEditForm.employeeId"
              placeholder="请选择"
              filterable
              style="width: 100%"
              @change="onDataEditEmployeeChange">
              <el-option
                v-for="item in dataEditEmployeeList"
                :key="item._id"
                :label="item.name"
                :value="item._id">
              </el-option>
            </el-select>
          </el-form-item>
          <!-- 日期选择（scheduledAt） -->
          <el-form-item
            v-if="currentDataEditNode.code === 'COMPANY_TRAINING_BOOK' || currentDataEditNode.code === 'PRACTICAL_TRAINING_BOOK'"
            label="约定时间"
            required>
            <el-date-picker
              v-model="dataEditForm.scheduledAt"
              type="datetime"
              placeholder="选择约定时间"
              value-format="yyyy-MM-dd HH:mm:ss"
              style="width: 100%;">
            </el-date-picker>
          </el-form-item>
          <!-- 日期选择（openingAt） -->
          <el-form-item
            v-if="currentDataEditNode.code === 'STORE_OPENING'"
            label="开业时间"
            required>
            <el-date-picker
              v-model="dataEditForm.openingAt"
              type="datetime"
              placeholder="选择开业时间"
              value-format="yyyy-MM-dd HH:mm:ss"
              style="width: 100%;">
            </el-date-picker>
          </el-form-item>
        </el-form>
      </div>
      <span slot="footer">
        <el-button @click="dataEditDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="dataEditLoading" @click="confirmDataEdit">保存</el-button>
      </span>
    </el-dialog>

    <!-- 建店资料收集编辑弹窗 -->
    <el-dialog
      title="编辑建店资料收集"
      :visible.sync="dataCollectEditDialogVisible"
      width="80%"
      top="5vh"
      :close-on-click-modal="false"
      @opened="onDataCollectEditOpened"
      @closed="onDataCollectEditClosed">
      <el-row :gutter="20">
        <!-- 左侧表单区域 -->
        <el-col :span="14">
          <el-form
            ref="dataCollectEditFormRef"
            :model="dataCollectEditForm"
            :rules="dataCollectEditRules"
            size="small"
            label-width="140px">
            <el-form-item label="经营门店地址" prop="storeAddress">
              <el-row :gutter="10">
                <el-col :span="16">
                  <el-input v-model="dataCollectEditForm.storeAddress" placeholder="请输入经营门店地址"></el-input>
                </el-col>
                <el-col :span="6">
                  <el-button type="primary" size="small" @click="autoLocateEdit">自动定位</el-button>
                </el-col>
              </el-row>
            </el-form-item>
            <el-form-item label="法人身份证姓名" prop="legalPersonName">
              <el-input v-model="dataCollectEditForm.legalPersonName" placeholder="请输入法人身份证姓名"></el-input>
            </el-form-item>
            <el-form-item label="法人身份证号码" prop="legalPersonId">
              <el-input v-model="dataCollectEditForm.legalPersonId" placeholder="请输入法人身份证号码"></el-input>
            </el-form-item>
            <el-form-item label="银行预留手机号" prop="bankPhone">
              <el-input v-model="dataCollectEditForm.bankPhone" placeholder="请输入银行预留手机号"></el-input>
            </el-form-item>
            <el-form-item label="银行开户行" prop="bankName">
              <el-input v-model="dataCollectEditForm.bankName" placeholder="请输入银行开户行"></el-input>
            </el-form-item>
            <el-form-item label="银行卡号" prop="bankAccount">
              <el-input v-model="dataCollectEditForm.bankAccount" placeholder="请输入银行卡号"></el-input>
            </el-form-item>
            <el-form-item label="店铺绑定号码" prop="storePhone">
              <el-input v-model="dataCollectEditForm.storePhone" placeholder="请输入店铺绑定号码"></el-input>
            </el-form-item>
            <el-form-item label="图片资料" prop="images">
              <ImageUpload
                ref="dataCollectEditUpload"
                v-model="dataCollectEditForm.images"
                :return-object="true"
                :active="dataCollectEditDialogVisible"
                :show-tip="true"
                @preview="handleUploadPreview"
              />
            </el-form-item>
            <el-form-item label="备注">
              <el-input
                type="textarea"
                :rows="3"
                placeholder="请输入备注"
                v-model="dataCollectEditForm.remark">
              </el-input>
            </el-form-item>
          </el-form>
        </el-col>
        <!-- 右侧地图区域 -->
        <el-col :span="10">
          <div class="map-info" v-if="dataCollectEditForm.province">
            {{ dataCollectEditForm.province }}{{ dataCollectEditForm.city }}{{ dataCollectEditForm.district }}
            | {{ dataCollectEditForm.lat?.toFixed(6) }}, {{ dataCollectEditForm.lng?.toFixed(6) }}
          </div>
          <div id="data-collect-edit-map" class="map-container"></div>
        </el-col>
      </el-row>
      <span slot="footer">
        <el-button @click="dataCollectEditDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="dataCollectEditLoading" @click="confirmDataCollectEdit">保存</el-button>
      </span>
    </el-dialog>

    <!-- 图片预览（隐藏的el-image组件） -->
    <el-image
      v-show="false"
      ref="imageViewer"
      :src="currentImageUrl"
      :preview-src-list="imagePreviewList">
    </el-image>
  </div>
</template>

<script>
import ChoujianMap from '@/utility/choujianMap'
import { hasAction } from '@/utility/permissions'
import html2canvas from 'html2canvas'
import ImageUpload from '@/components/ImageUpload.vue'

// 首批物料备忘统一内容判空：兼容 undefined（历史文档）与空对象两种形态
const hasMemo = m => !!((m && m.text && m.text.trim()) || (m && m.images && m.images.length))

export default {
  name: 'ChoujianDetail',
  components: { ImageUpload },
  data () {
    return {
      // 权限数据就位信号（Home 分发 grantedActions:ready 事件时自增，触发显隐重算）
      grantedTick: 0,
      // 页面加载状态
      pageLoading: false,

      // 详情数据
      currentDetail: null,

      // 上传相关配置
      uploadUrl: this.$http.defaults.baseURL + 'upload',
      uploadHeaders: {
        authorization: window.sessionStorage.getItem('token'),
        Accept: 'application/json'
      },
      base: window.sessionStorage.getItem('base') || '',

      // 地图相关
      map: null,
      script: null,
      scriptLoading: null,

      // ===== 通用节点操作弹窗 =====
      nodeActionDialogVisible: false,
      nodeActionLoading: false,
      currentActionNode: null,
      nodeActionForm: {},
      nodeActionFileList: {},
      currentEmployeeList: [],
      // 财务门店映射表的最新编码（详情接口返回；MEITUAN_ORDER_REGISTER 表单预填/编辑回显的权威源）
      scmCodes: { meituanCode: '', shihengCode: '' },
      completeLoopLoading: false,
      legalCheckLoading: false,
      legalCheckResult: null, // null: 未检测, true: 通过, false: 不通过

      // 节点过滤
      showCompletedNodes: true,

      // 节点详情弹窗
      nodeDetailDialogVisible: false,
      currentDetailNode: null,

      // ===== 实操培训答题结果 =====
      currentExamResult: null,

      // ===== 图片预览 =====
      imagePreviewList: [],
      currentImageUrl: '',

      // ===== 图片编辑弹窗 =====
      imageEditDialogVisible: false,
      imageEditLoading: false,
      currentEditNode: null,
      imageEditForm: {
        images: [],
        remark: '',
        meituanCode: '',
        shihengCode: ''
      },

      // ===== 产品审核记录编辑 =====
      productReviewEditDialogVisible: false,
      productReviewEditLoading: false,
      currentEditProductReview: null,
      productReviewEditForm: {
        images: [],
        remark: ''
      },

      // ===== 数据编辑弹窗（人员重新选择 / 日期修改） =====
      dataEditDialogVisible: false,
      dataEditLoading: false,
      currentDataEditNode: null,
      dataEditForm: {},
      dataEditEmployeeList: [],

      // ===== 建店资料收集编辑弹窗 =====
      dataCollectEditDialogVisible: false,
      dataCollectEditLoading: false,
      dataCollectEditForm: {},
      dataCollectEditMap: null,

      // 阶段配置列表（根据流程模板定义）
      stageList: [
        { code: 'RECEIVE', name: '接收阶段', nodeCodes: ['RECEIVE', 'FIRST_COMMUNICATION'] },
        { code: 'DESIGN', name: '设计阶段', nodeCodes: ['DOOR_DESIGN_DECISION', 'DOOR_DESIGN_NOTIFY', 'DOOR_DESIGN_COMPLETE', 'DOOR_DESIGN_END', 'LAYOUT_DESIGN_DECISION', 'LAYOUT_DESIGN_NOTIFY', 'LAYOUT_DESIGN_COMPLETE', 'LAYOUT_DESIGN_REVIEW', 'LAYOUT_DESIGN_END'] },
        { code: 'PREPARE', name: '准备阶段', nodeCodes: ['COMPANY_TRAINING_BOOK', 'COMPANY_TRAINING_COMPLETE', 'EQUIPMENT_LIST', 'MATERIAL_LIST', 'MEITUAN_ORDER_REGISTER', 'FIRST_BATCH_REVIEW', 'BUSINESS_LICENSE', 'FOOD_LICENSE', 'CAMERA_SHARE', 'ONLINE_CHECK'] },
        { code: 'TRAINING', name: '培训操作阶段', nodeCodes: ['PRACTICAL_TRAINING_BOOK', 'PRACTICAL_TRAINING_COMPLETE', 'PRODUCT_AUDIT'] },
        { code: 'DATA_COLLECT', name: '资料收集', nodeCodes: ['DATA_COLLECT'] },
        { code: 'ACCOUNT', name: '账号阶段', nodeCodes: ['ACCOUNT_NOTIFY', 'ACCOUNT_CREATE', 'ASSIGN_OPERATOR'] },
        { code: 'AGREEMENT', name: '协议阶段', nodeCodes: ['MANAGEMENT_AGREEMENT', 'STORE_OPENING'] },
        { code: 'PRODUCT_REVIEW', name: '产品审核', nodeCodes: ['PRODUCT_REVIEW', 'FINISH_BUILD'] }
      ],
      // 可编辑人员/日期的节点编码列表（管理员可重新编辑已完成的人员选择/日期节点）
      employeeReeditableNodes: [
        'DOOR_DESIGN_NOTIFY',
        'LAYOUT_DESIGN_NOTIFY',
        'COMPANY_TRAINING_BOOK',
        'PRACTICAL_TRAINING_BOOK',
        'ASSIGN_OPERATOR',
        'STORE_OPENING'
      ]
    }
  },
  computed: {
    // 项目首批物料备忘（详情接口 populate projectId 自动下发；历史项目字段为 undefined）
    projectMemo () {
      const p = this.currentDetail && this.currentDetail.instance && this.currentDetail.instance.projectId
      return (p && p.firstMaterialMemo) || {}
    },

    // 当前激活的节点（状态为 IN_PROGRESS 且 order 最小的）
    currentActiveNode () {
      if (!this.currentDetail || !this.currentDetail.nodes) return null
      const activeNodes = this.currentDetail.nodes.filter(n => n.status === 'IN_PROGRESS')
      if (activeNodes.length === 0) return null
      return activeNodes.sort((a, b) => a.order - b.order)[0]
    },

    // 是否需要地图（DATA_COLLECT节点）
    needsMap () {
      return this.currentActionNode?.code === 'DATA_COLLECT'
    },

    // 美团订货注册：编码回显基准（财务门店映射表为唯一权威源，不回退节点旧值——财务侧修改不会回填节点数据）
    scmCodeBaseline () {
      return {
        meituanCode: String(this.scmCodes?.meituanCode || '').trim(),
        shihengCode: String(this.scmCodes?.shihengCode || '').trim()
      }
    },

    // 过滤后的 dataFields（排除人员选择字段、隐藏字段、地图自动填充字段）
    filteredDataFields () {
      if (!this.currentActionNode?.dataFields) return []
      const mapAutoFields = ['lat', 'lng', 'province', 'city', 'district']
      return this.currentActionNode.dataFields.filter(
        f => f.fieldCode !== 'employeeId' &&
             f.fieldCode !== 'employeeName' &&
             !f.hidden &&
             !(this.needsMap && mapAutoFields.includes(f.fieldCode))
      )
    },

    // 动态验证规则
    nodeActionRules () {
      const rules = {}
      if (this.currentActionNode?.employeeSelection) {
        rules.employeeId = [{ required: true, message: '请选择人员', trigger: 'change' }]
      }
      this.filteredDataFields.forEach(field => {
        if (field.required) {
          rules[field.fieldCode] = [{ required: true, message: `请填写${field.fieldName}`, trigger: 'blur' }]
        }
      })
      return rules
    },

    // 循环节点是否可以完成
    canCompleteLoop () {
      const node = this.currentActionNode
      if (node?.type !== 'LOOP') return false
      const minCount = node.loopConfig?.minCount || 1
      return node.loopCount >= minCount
    },

    // 当前操作节点是否为实操培训完成节点
    isPracticalTrainingNode () {
      return this.currentActionNode?.code === 'PRACTICAL_TRAINING_COMPLETE'
    },

    // FEEDBACK 节点的审核内容（被审核节点提交的设计稿）
    feedbackReviewContent () {
      if (!this.currentActionNode || this.currentActionNode.type !== 'FEEDBACK') return null
      if (!this.currentDetail?.nodes) return null
      const targetCode = this.currentActionNode.feedbackConfig?.targetNode
      if (!targetCode) return null
      const targetNode = this.currentDetail.nodes.find(n => n.code === targetCode)
      if (!targetNode) return null
      return {
        images: targetNode.data?.images || [],
        remark: targetNode.data?.remark || '',
        feedbackRecords: targetNode.feedbackRecords || [],
        nodeName: targetNode.name,
        assigneeName: targetNode.assigneeName
      }
    },

    // 过滤后的节点列表（根据开关控制是否显示已完成节点）
    filteredNodes () {
      if (!this.currentDetail?.nodes) return []
      if (this.showCompletedNodes) return this.currentDetail.nodes
      return this.currentDetail.nodes.filter(
        n => n.status !== 'COMPLETED' && n.status !== 'SKIPPED'
      )
    },

    // 当前用户员工ID
    currentEmployeeId () {
      return window.sessionStorage.getItem('employeeId') || ''
    },

    // 筹建中详情地址
    detailAddress () {
      if (!this.currentDetail) return ''
      // 优先从 DATA_COLLECT 节点获取已填写的地址
      const dataCollectNode = (this.currentDetail.nodes || []).find(n => n.code === 'DATA_COLLECT')
      if (dataCollectNode?.data?.storeAddress) return dataCollectNode.data.storeAddress
      // 其次从 project/store 获取
      const storeMap = this.currentDetail.project?.storeMap || this.currentDetail.store?.storeMap
      if (storeMap?.address) return storeMap.address
      return this.currentDetail.store?.address || ''
    },

    // 是否有图片编辑权限：与"更正/改判决"同源，后端闸门是 need('dudaoChoujian','修改')
    // （G13：不再按 job/level 猜身份，改读登录下发的"模块→已授予动作"）
    canEditImages () {
      const tick = this.grantedTick // 响应性依赖：权限数据就位后重算
      return tick >= 0 && hasAction('dudaoChoujian', '修改')
    },

    // 是否有修改决策权限
    canModifyDecision () {
      const tick = this.grantedTick
      return tick >= 0 && hasAction('dudaoChoujian', '修改')
    },

    // DATA_COLLECT 编辑表单验证规则
    dataCollectEditRules () {
      return {
        storeAddress: [{ required: true, message: '请输入经营门店地址', trigger: 'blur' }],
        legalPersonName: [{ required: true, message: '请输入法人身份证姓名', trigger: 'blur' }],
        legalPersonId: [{ required: true, message: '请输入法人身份证号码', trigger: 'blur' }],
        bankPhone: [{ required: true, message: '请输入银行预留手机号', trigger: 'blur' }],
        bankName: [{ required: true, message: '请输入银行开户行', trigger: 'blur' }],
        bankAccount: [{ required: true, message: '请输入银行卡号', trigger: 'blur' }],
        storePhone: [{ required: true, message: '请输入店铺绑定号码', trigger: 'blur' }]
      }
    },

    // 可编辑图片的节点编码列表
    editableNodeCodes () {
      return [
        'FIRST_COMMUNICATION',
        'DOOR_DESIGN_COMPLETE',
        'LAYOUT_DESIGN_COMPLETE',
        'COMPANY_TRAINING_COMPLETE',
        'EQUIPMENT_LIST',
        'MATERIAL_LIST',
        'MEITUAN_ORDER_REGISTER',
        'FIRST_BATCH_REVIEW',
        'BUSINESS_LICENSE',
        'FOOD_LICENSE',
        'CAMERA_SHARE',
        'ONLINE_CHECK',
        'PRACTICAL_TRAINING_COMPLETE',
        'PRODUCT_AUDIT',
        'MANAGEMENT_AGREEMENT'
      ]
    },

    // 资料收集节点数据
    dataCollectNode () {
      if (!this.currentDetail?.nodes) return null
      return this.currentDetail.nodes.find(n => n.code === 'DATA_COLLECT')
    },

    // 平台账号相关节点数据
    accountNodes () {
      if (!this.currentDetail?.nodes) return {}
      const notify = this.currentDetail.nodes.find(n => n.code === 'ACCOUNT_NOTIFY')
      const create = this.currentDetail.nodes.find(n => n.code === 'ACCOUNT_CREATE')
      const assign = this.currentDetail.nodes.find(n => n.code === 'ASSIGN_OPERATOR')
      return { notify, create, assign }
    },

    // 产品审核节点数据
    productReviewNode () {
      if (!this.currentDetail?.nodes) return null
      return this.currentDetail.nodes.find(n => n.code === 'PRODUCT_REVIEW')
    },

    // 营业执照节点确定按钮是否可提交
    canSubmitBusinessLicense () {
      // 仅对营业执照节点生效
      if (!this.currentActionNode || this.currentActionNode.code !== 'BUSINESS_LICENSE') {
        return true // 其他节点始终可提交
      }

      // 未检测：禁用
      if (this.legalCheckResult === null) {
        return false
      }

      // 检测通过：可提交
      if (this.legalCheckResult === true) {
        return true
      }

      // 检测不通过：需图片数 > 1 才可提交
      const images = this.nodeActionForm.images || []
      return images.length > 1
    }
  },
  created () {
    this.base = window.sessionStorage.getItem('base')
    // 权限数据就位时重算显隐：computed 直读 sessionStorage 无响应性，
    // 深链直达本页时 menus 可能尚未返回，靠 Home 的 ready 事件触发重算
    this._onGrantedReady = () => { this.grantedTick++ }
    window.addEventListener('grantedActions:ready', this._onGrantedReady)
    this.loadInstanceDetail()
    this.loadScript()
  },
  destroyed () {
    window.removeEventListener('grantedActions:ready', this._onGrantedReady)
    this.unloadScript()
  },
  methods: {
    // ==================== 答题结果辅助方法 ====================
    /**
     * 判断答题结果是否满足完成条件（兼容旧后端无 satisfied 字段的情况）
     * @param {Object} result - 答题结果对象
     * @return {Boolean} 是否满足完成条件
     */
    isExamSatisfied (result) {
      if (!result) return false
      return result.satisfied !== undefined ? result.satisfied : result.allPassed
    },

    /**
     * 生成答题结果 tag 文案
     * @param {Object} result - 答题结果对象
     * @return {String} tag 文案（含总人数后缀）
     */
    examResultTagText (result) {
      if (!result || !result.list) return '-'
      const total = result.list.length
      const passedCount = result.list.filter(r => r.passed).length
      const failedCount = total - passedCount
      const satisfied = this.isExamSatisfied(result)
      const strategy = result.passStrategy || 'all'
      const suffix = `（共${total}人）`
      if (satisfied) {
        if (strategy === 'one') {
          return `已通过${passedCount}人${suffix}`
        }
        return `全部通过${suffix}`
      }
      if (strategy === 'one') {
        return `无人通过${suffix}`
      }
      return `${failedCount}人未通过${suffix}`
    },

    // ==================== 页面核心方法 ====================

    // 首批物料备忘截图地址（相对路径拼附件基地址）
    getMemoImg (img) {
      return this.base + img
    },

    // 统一内容判空（模板可用）
    hasMemo,

    // 加载流程实例详情
    async loadInstanceDetail () {
      const instanceId = this.$route.params.instanceId
      if (!instanceId) {
        this.$message.error('缺少实例ID参数')
        return
      }

      this.pageLoading = true
      try {
        const { data: res } = await this.$http.get(`process/instances/${instanceId}`, {
          params: { includeLogs: true }
        })
        if (res.meta.status !== 200) {
          this.$message.error(res.meta.msg)
          return
        }
        this.currentDetail = res.data
        this.scmCodes = res.data.scmCodes || { meituanCode: '', shihengCode: '' }
      } catch (error) {
        console.error('获取详情失败:', error)
        this.$message.error('获取筹建详情失败')
      } finally {
        this.pageLoading = false
      }
    },

    // 刷新详情数据
    async refreshDetail () {
      const instanceId = this.$route.params.instanceId || this.currentDetail?.instance?._id
      if (!instanceId) {
        return this.$message.error('找不到实例ID')
      }
      const loading = this.$loading({
        lock: true,
        text: '刷新中...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })
      try {
        const { data: res } = await this.$http.get(`process/instances/${instanceId}`, {
          params: { includeLogs: true }
        })
        if (res.meta.status === 200) {
          this.currentDetail = res.data
          // 同步刷新映射表编码基准（权威源，防刷新后回显过期码被写回映射表）
          this.scmCodes = res.data.scmCodes || { meituanCode: '', shihengCode: '' }
          this.$message.success('刷新成功')
        } else {
          this.$message.error(res.meta.msg)
        }
      } catch (error) {
        console.error('刷新详情失败:', error)
        this.$message.error('刷新失败')
      } finally {
        loading.close()
      }
    },

    // 返回工作台
    goBack () {
      this.$router.push('/dudao/choujian')
    },

    // ==================== 详情辅助方法 ====================

    // 从 assignees 数组中获取指定角色的名称
    getAssigneeName (assignees, role) {
      if (!assignees || !Array.isArray(assignees)) return '-'
      const person = assignees.find(a => a.role === role)
      return person?.employeeName || '-'
    },

    // 根据进度百分比返回颜色
    getProgressColor (percentage) {
      if (percentage >= 100) return '#67C23A'
      if (percentage >= 60) return '#409EFF'
      if (percentage >= 30) return '#E6A23C'
      return '#F56C6C'
    },

    // 获取阶段进度百分比
    getStagePercentage (stage) {
      if (!this.currentDetail || !this.currentDetail.nodes) return 0
      const stageNodes = this.currentDetail.nodes.filter(n => stage.nodeCodes.includes(n.code))
      if (stageNodes.length === 0) return 0
      const completed = stageNodes.filter(n => n.status === 'COMPLETED' || n.status === 'SKIPPED').length
      return Math.round((completed / stageNodes.length) * 100)
    },

    // 获取阶段样式类
    getStageClass (stage) {
      const percentage = this.getStagePercentage(stage)
      if (percentage >= 100) return 'stage-complete'
      if (percentage > 0) return 'stage-processing'
      return 'stage-pending'
    },

    // 获取阶段状态文字
    getStageStatusText (stage) {
      if (!this.currentDetail || !this.currentDetail.nodes) return '待开始'
      const stageNodes = this.currentDetail.nodes.filter(n => stage.nodeCodes.includes(n.code))
      if (stageNodes.length === 0) return '待开始'
      const completed = stageNodes.filter(n => n.status === 'COMPLETED' || n.status === 'SKIPPED').length
      if (completed === 0) return '待开始'
      if (completed === stageNodes.length) return `${completed}/${stageNodes.length} 完成`
      return `${completed}/${stageNodes.length} 完成`
    },

    // 获取阶段进度条颜色
    getStageColor (stage) {
      const percentage = this.getStagePercentage(stage)
      if (percentage >= 100) return '#67C23A'
      if (percentage > 0) return '#E42313'
      return '#E8E8E8'
    },

    // 计算门店开业天数
    getOpenedDays (completedAt) {
      if (!completedAt) return 0
      const completed = new Date(completedAt)
      const today = new Date()
      completed.setHours(0, 0, 0, 0)
      today.setHours(0, 0, 0, 0)
      return Math.floor((today - completed) / (86400000))
    },

    // ==================== 节点状态方法 ====================

    // 获取节点状态类型（用于 el-tag）
    getNodeStatusType (status) {
      const typeMap = {
        PENDING: 'info',
        IN_PROGRESS: 'danger',
        IN_MODIFYING: 'danger',
        COMPLETED: 'success',
        SKIPPED: 'warning',
        RETURNED: 'warning'
      }
      return typeMap[status] || 'info'
    },

    // 获取节点状态文字
    getNodeStatusText (node) {
      if (node.type === 'LOOP') {
        if (node.status === 'COMPLETED') return `完成 (${node.loopCount}次)`
        if (node.status === 'IN_PROGRESS') return `${node.loopCount}/${node.loopConfig?.minCount || 1}次`
      }
      const textMap = {
        PENDING: '待处理',
        IN_PROGRESS: '进行中',
        IN_MODIFYING: '修改中',
        COMPLETED: '已完成',
        SKIPPED: '已跳过',
        RETURNED: '已退回'
      }
      return textMap[node.status] || '未知'
    },

    // 判断节点是否可执行操作
    canExecuteNode (node) {
      if (node.status === 'IN_PROGRESS') return true
      if (node.status === 'IN_MODIFYING') return true
      if (node.type === 'LOOP' && (node.status === 'IN_PROGRESS')) return true
      if (node.type === 'BRANCH' && node.status === 'IN_PROGRESS') return true
      return false
    },

    // 判断当前用户是否是节点负责人
    isNodeAssignee (node) {
      if (!this.currentEmployeeId) return false
      return node.assigneeId === this.currentEmployeeId
    },

    // 节点详情只读展示取值：美团订货注册的编码字段以财务门店映射最新值为准
    // （project_scm_maps 为唯一权威源，node.data 仅为提交时快照，财务改码后不回填）
    getNodeDataDisplayValue (field) {
      if (this.currentDetailNode?.code === 'MEITUAN_ORDER_REGISTER' &&
        (field.fieldCode === 'meituanCode' || field.fieldCode === 'shihengCode')) {
        return this.scmCodeBaseline[field.fieldCode]
      }
      return this.currentDetailNode.data[field.fieldCode]
    },

    // 查看节点详情
    handleNodeDetail (node) {
      this.currentDetailNode = node
      this.nodeDetailDialogVisible = true
    },

    // 获取节点操作按钮文字
    getNodeActionText (node) {
      if (node.type === 'LOOP') return '添加记录'
      if (node.type === 'BRANCH') return '决策'
      return '提交'
    },

    // 处理节点行点击
    handleNodeRowClick () {
      // 预留：可扩展节点详情查看
    },

    // 修改决策节点（管理员、运营总监、督导主管）
    async handleModifyDecision (node) {
      try {
        await this.$confirm('修改决策将重置所有相关节点的工作记录，是否继续？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        // 打开决策选择弹窗（复用现有的节点操作弹窗）
        this.currentActionNode = node
        this.nodeActionForm = { decisionValue: node.data.decisionValue }
        this.nodeActionDialogVisible = true
      } catch {
        // 用户取消
      }
    },

    // ==================== 节点操作弹窗方法 ====================

    // 处理节点执行 - 打开通用节点操作弹窗
    async handleNodeExecute (node) {
      this.currentActionNode = node
      this.nodeActionForm = {}
      this.nodeActionFileList = {}
      this.currentExamResult = null
      this.legalCheckResult = null // 重置法人检测结果
      this.nodeActionDialogVisible = true

      // 如果是人员选择节点，获取员工列表
      if (node.employeeSelection) {
        try {
          const { data: res } = await this.$http.get('process/getEmployeeList', {
            params: { role: node.employeeSelection.role }
          })
          if (res.meta.status === 200) {
            this.currentEmployeeList = res.data || []
          }
        } catch (error) {
          console.error('获取员工列表失败:', error)
          this.currentEmployeeList = []
        }
      }

      // 实操培训完成节点：初始化响应式数组并加载答题结果
      if (node.code === 'PRACTICAL_TRAINING_COMPLETE') {
        this.$set(this.nodeActionForm, 'images', [])
        this.$set(this.nodeActionFileList, 'images', [])
        // projectId 可能是字符串或被 populate 的对象
        const rawProjectId = this.currentDetail?.instance?.projectId
        const projectId = rawProjectId?._id || rawProjectId
        if (projectId) {
          try {
            const { data: res } = await this.$http.get('tiku/record/summary', {
              params: { projectId }
            })
            if (res.meta.status === 200) {
              this.currentExamResult = res.data
            }
          } catch (e) {
            console.error('获取答题结果失败:', e)
          }
        }
      }

      // 营业执照节点：初始化响应式数组（确保 Vue 2 能追踪 images 变化）
      if (node.code === 'BUSINESS_LICENSE') {
        this.$set(this.nodeActionForm, 'images', [])
        this.$set(this.nodeActionFileList, 'images', [])
      }

      // 美团订货注册：预填财务门店映射表的最新编码（权威源，督导核对确认后提交）
      if (node.code === 'MEITUAN_ORDER_REGISTER') {
        this.$set(this.nodeActionForm, 'meituanCode', this.scmCodeBaseline.meituanCode)
        this.$set(this.nodeActionForm, 'shihengCode', this.scmCodeBaseline.shihengCode)
      }
    },

    // 员工选择变更
    onEmployeeChange (employeeId) {
      const emp = this.currentEmployeeList.find(e => e._id === employeeId)
      this.nodeActionForm.employeeId = employeeId
      this.nodeActionForm.employeeName = emp?.name || ''
    },

    // 法人检测
    async handleLegalPersonCheck () {
      const images = this.nodeActionForm.images
      if (!images || images.length === 0) {
        return this.$message.warning('请先上传营业执照图片')
      }

      // 取第一张图片路径（兼容字符串和 { url, hash } 对象格式）
      const firstImage = images[0]
      const imgPath = typeof firstImage === 'string' ? firstImage : firstImage.url

      this.legalCheckLoading = true
      try {
        const instanceId = this.currentDetail?.instance?._id
        const { data: res } = await this.$http.post('process/legalPersonCheck', {
          instanceId,
          img: imgPath
        })
        if (res.meta.status === 200) {
          // 保存检测结果
          this.legalCheckResult = res.data.matched

          if (res.data.matched) {
            this.$message.success(res.meta.msg)
          } else {
            this.$message.warning(res.meta.msg)
          }
        } else {
          this.$message.error(res.meta.msg)
        }
      } catch (error) {
        this.$message.error('法人检测失败，请稍后重试')
      } finally {
        this.legalCheckLoading = false
      }
    },

    // 获取弹窗标题
    getNodeActionTitle () {
      if (!this.currentActionNode) return '节点操作'
      const node = this.currentActionNode
      if (node.type === 'LOOP') {
        return `${node.name} - 添加记录 (${node.loopCount || 0}/${node.loopConfig?.minCount || 1})`
      }
      return node.name
    },

    // 弹窗打开后
    onNodeDialogOpened () {
      if (this.needsMap) {
        this.$nextTick(() => this.initMapForDataCollect())
      }
    },

    // 弹窗关闭后
    onNodeDialogClosed () {
      if (this.map) {
        this.map.removeAllListeners('click')
        this.map.destroy()
        this.map = null
      }
      this.currentActionNode = null
      this.nodeActionForm = {}
      this.nodeActionFileList = {}
      this.currentEmployeeList = []
      this.legalCheckResult = null // 重置法人检测结果
    },

    // ==================== 上传相关方法 ====================

    // 上传图片预览
    handleUploadPreview (file) {
      let imageUrl = ''
      if (file.url) {
        imageUrl = file.url
      } else if (file.response && file.response.data && file.response.data.url) {
        imageUrl = this.base + file.response.data.url
      }
      if (imageUrl) {
        this.imagePreviewList = [imageUrl]
        this.currentImageUrl = imageUrl
        this.$nextTick(() => {
          this.$refs.imageViewer.clickHandler()
        })
      }
    },

    // 图片上传成功（来自 ImageUpload 的 upload-success 事件）
    handleNodeUploadSuccess (imageData, fieldCode) {
      // ImageUpload 组件已通过 v-model 更新 nodeActionForm[fieldCode]
      // 这里只需要做额外的处理（如日志记录等）
      console.log('图片上传成功:', imageData.url, 'hash:', imageData.hash)
    },

    // 图片移除（已由 ImageUpload 内部处理，此方法保留备用）
    handleNodeUploadRemove (file, fileList, fieldCode) {
      // ImageUpload 组件内部处理移除逻辑，通过 v-model 自动更新
    },

    // 文件上传成功
    handleNodeFileSuccess (res, file, fileList, fieldCode) {
      if (res.meta.status !== 200) {
        this.$message.error(res.meta.msg)
        return
      }
      if (!this.nodeActionForm[fieldCode]) {
        this.nodeActionForm[fieldCode] = []
      }
      // 改造后：保存 url 和 hash
      this.nodeActionForm[fieldCode].push({
        url: res.data.url,
        hash: res.data.hash || null
      })
      this.$set(this.nodeActionFileList, fieldCode, fileList)
    },

    // 文件移除
    handleNodeFileRemove (file, fileList, fieldCode) {
      // 找到被删除文件的 URL
      const removedUrl = file.response?.data?.url || file.url

      // 从 nodeActionForm 中删除匹配的项（按 url 匹配，兼容字符串和对象格式）
      if (this.nodeActionForm[fieldCode]) {
        const index = this.nodeActionForm[fieldCode].findIndex(item => {
          const itemUrl = typeof item === 'string' ? item : item.url
          return itemUrl === removedUrl
        })
        if (index !== -1) this.nodeActionForm[fieldCode].splice(index, 1)
      }
      this.$set(this.nodeActionFileList, fieldCode, fileList)
    },

    // ==================== 节点操作确认方法 ====================

    // 确认节点操作
    async confirmNodeAction (actionOverride) {
      const valid = await this.$refs.nodeActionFormRef?.validate().catch(() => false)
      if (!valid) return

      // 美团订货注册：供应链编码至少填一个（跨字段联合校验，后端引擎同步兑底）
      if (this.currentActionNode && this.currentActionNode.code === 'MEITUAN_ORDER_REGISTER') {
        const m = String(this.nodeActionForm.meituanCode || '').trim()
        const s = String(this.nodeActionForm.shihengCode || '').trim()
        if (!m && !s) {
          return this.$message.warning('请至少填写一个供应链编码（美团管家编码或食亨编码）')
        }
      }

      // 实操培训：验证答题结果
      if (this.isPracticalTrainingNode && this.currentExamResult) {
        if (this.currentExamResult.list.length === 0) {
          return this.$message.warning('暂无员工答题记录，无法完成')
        }
        const satisfied = this.isExamSatisfied(this.currentExamResult)
        if (!satisfied) {
          if (this.currentExamResult.passStrategy === 'one') {
            return this.$message.warning('暂无员工通过实操测试，至少需要一人通过才能完成')
          }
          const failedCount = this.currentExamResult.list.filter(r => !r.passed).length
          return this.$message.warning(`还有${failedCount}人未通过实操测试，所有人都通过后才能完成`)
        }
      }

      this.nodeActionLoading = true
      try {
        const instanceId = this.currentDetail?.instance?._id
        const node = this.currentActionNode

        // 确定操作类型
        const isValidAction = typeof actionOverride === 'string'
        let action = isValidAction ? actionOverride : 'complete'
        if (!isValidAction) {
          if (node.type === 'LOOP') action = 'loop'
          if (node.type === 'BRANCH') action = 'decision'
          if (node.type === 'FEEDBACK') action = 'feedback'
        }

        // 如果当前节点已完成且是决策节点，则使用 modifyDecision
        if (node.status === 'COMPLETED' && node.type === 'BRANCH') {
          // 检查决策值是否真的修改了
          const originalValue = node.data?.decisionValue
          const newValue = this.nodeActionForm.decisionValue
          if (originalValue === newValue) {
            this.$message.warning('决策值未发生变化，无需修改')
            return
          }
          action = 'modifyDecision'
        }

        // 转让重开的客户信息节点：提交前二次确认，避免直接沿用旧资料
        if (action === 'complete' && node.data && node.data.transferReopen) {
          try {
            await this.$confirm(
              '请确认已按受让人重新填写/上传全部资料。',
              '转让节点确认',
              { type: 'warning', confirmButtonText: '已更新，提交', cancelButtonText: '取消' }
            )
          } catch (e) {
            return
          }
        }

        // modifyDecision 属"上级更正"能力，与 reupdate 一同从普通执行口移出，单独走 superExecute
        const endpoint = action === 'modifyDecision' ? 'superExecute' : 'execute'
        const { data: res } = await this.$http.post(`process/instances/${instanceId}/${endpoint}`, {
          nodeCode: node.code,
          action,
          data: this.nodeActionForm
        })

        if (res.meta.status !== 200) {
          this.$message.error(res.meta.msg)
          return
        }

        this.$message.success('操作成功')
        this.nodeActionDialogVisible = false
        // 刷新详情数据
        await this.loadInstanceDetail()
      } catch (error) {
        // 图片重复拦截处理
        const resData = error.response?.data
        if (resData?.meta?.status === 400 && resData?.meta?.msg?.includes('图片重复')) {
          const info = resData.data?.duplicateInfo
          if (info) {
            const dateStr = this.$options.filters.dateFormat(info.existingUploadedAt)
            const loopIndexText = info.existingLoopIndex ? `第 ${info.existingLoopIndex} 次` : ''
            this.$message.error(
              `检测到图片重复！该图片已于 ${dateStr} 在【${info.existingStoreName}】的【${info.existingNodeName}】${loopIndexText}审核中使用，上传人：${info.existingUploadedBy}。请重新拍摄上传新的图片。`,
              { duration: 8000 }
            )
          } else {
            this.$message.error('检测到图片重复，请重新上传新的图片', { duration: 5000 })
          }
        } else {
          this.$message.error('操作失败，请稍后重试')
        }
      } finally {
        this.nodeActionLoading = false
      }
    },

    // 自动截图答题结果并上传
    async captureAndUploadExamResult () {
      if (!this.$refs.examResultCapture) {
        this.$message.warning('暂无答题结果可截图')
        return
      }

      try {
        const canvas = await html2canvas(this.$refs.examResultCapture, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true
        })

        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'))

        const formData = new FormData()
        formData.append('file', blob, '答题结果截图.png')

        const { data: res } = await this.$http.post('upload', formData, {
          headers: {
            authorization: window.sessionStorage.getItem('token'),
            Accept: 'application/json',
            'Content-Type': 'multipart/form-data'
          }
        })

        if (res.meta.status !== 200) {
          this.$message.error(res.meta.msg || '截图上传失败')
          return
        }

        // 使用 ImageUpload 组件的 addImageUrl 方法添加图片
        const imageUploadRef = this.$refs.imageUpload_images
        if (imageUploadRef && imageUploadRef[0]) {
          // ref 是数组形式（因为是在 v-for 中）
          imageUploadRef[0].addImageUrl(res.data.url, res.data.hash, '答题结果截图.png')
        } else {
          // 备用方案：直接添加到 nodeActionForm
          this.nodeActionForm.images.push({ url: res.data.url, hash: res.data.hash })
          this.$message.success('答题结果截图已上传')
        }
      } catch (error) {
        console.error('自动截图失败:', error)
        this.$message.error('自动截图失败，请手动上传')
      }
    },

    // 完成循环节点
    async confirmCompleteLoop () {
      const instanceId = this.currentDetail?.instance?._id
      const node = this.currentActionNode

      this.completeLoopLoading = true
      try {
        const { data: res } = await this.$http.post(`process/instances/${instanceId}/complete-loop`, {
          nodeCode: node.code
        })
        if (res.meta.status !== 200) {
          this.$message.error(res.meta.msg)
          return
        }
        this.$message.success('节点已完成')
        this.nodeActionDialogVisible = false
        // 刷新详情数据
        await this.loadInstanceDetail()
      } finally {
        this.completeLoopLoading = false
      }
    },

    // 显示节点历史
    showNodeHistory () {
      this.$message.info('查看历史记录')
    },

    // 执行当前节点
    executeCurrentNode () {
      if (this.currentActiveNode) {
        this.handleNodeExecute(this.currentActiveNode)
      }
    },

    // ==================== 地图相关方法 ====================

    // 地图初始化（DATA_COLLECT节点）
    initMapForDataCollect () {
      const storeMap = this.currentDetail?.project?.storeMap || this.currentDetail?.store?.storeMap || {}
      const lat = storeMap.lat || 34.309615
      const lng = storeMap.lng || 108.923302

      this.map = new ChoujianMap('node-map-container', lat, lng)
      this.map.addPoint(lat, lng)
      this.map.on('click', (lat, lng) => {
        this.nodeActionForm.lat = lat
        this.nodeActionForm.lng = lng
      })
    },

    // 自动定位
    async autoLocate () {
      const address = this.nodeActionForm.storeAddress
      if (!address) {
        this.$message.warning('请先输入门店地址')
        return
      }
      const { data: res } = await this.$http.get('map/geocoder', { params: { address } })
      if (res.meta.status !== 200) return this.$message.error(res.meta.msg)

      this.nodeActionForm.lat = res.data.location.lat
      this.nodeActionForm.lng = res.data.location.lng
      this.nodeActionForm.province = res.data.address_components.province
      this.nodeActionForm.city = res.data.address_components.city
      this.nodeActionForm.district = res.data.address_components.district
      this.map.setPoint(res.data.location.lat, res.data.location.lng)
    },

    // 加载地图脚本
    loadScript () {
      this.scriptLoading = this.$loading({
        lock: true,
        text: 'Loading',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })
      window.removeLoading = this.removeLoading
      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.src = `https://map.qq.com/api/gljs?v=1.exp&key=${ChoujianMap.apiKey}&callback=removeLoading`
      document.body.appendChild(script)
      this.script = script
    },

    // 移除地图加载遮罩
    removeLoading () {
      this.scriptLoading.close()
    },

    // 卸载地图脚本
    unloadScript () {
      if (this.script) {
        document.body.removeChild(this.script)
        this.script = null
        this.map = null
        delete window.TMap
        delete window.removeLoading
      }
    },

    // ==================== 图片编辑功能 ====================

    // 统一编辑入口：根据节点类型分发到不同弹窗
    handleEditNodeClick (node) {
      if (this.employeeReeditableNodes.includes(node.code)) {
        this.handleDataEditNode(node)
      } else {
        this.handleEditNodeImages(node)
      }
    },

    // 判断节点是否可编辑图片
    canEditNodeImages (node) {
      if (!this.canEditImages) return false
      if (node.status !== 'COMPLETED' && node.status !== 'SKIPPED') return false
      return this.editableNodeCodes.includes(node.code)
    },

    // 判断节点是否可编辑人员/日期
    canEditNodeAssignment (node) {
      if (!this.canEditImages) return false
      if (node.status !== 'COMPLETED' && node.status !== 'SKIPPED') return false
      return this.employeeReeditableNodes.includes(node.code)
    },

    // 打开节点图片编辑弹窗
    handleEditNodeImages (node) {
      this.currentEditNode = node
      // 将 URL 字符串数组转换为对象数组格式（支持 hash）
      const images = node.data?.images || []
      this.imageEditForm.images = images.map(img => {
        if (typeof img === 'string') {
          return { url: img, hash: null }
        }
        return img // 如果已经是对象格式，直接使用
      })
      this.imageEditForm.remark = node.data?.remark || ''
      // 美团订货注册：回显财务门店映射表的最新编码（权威源；提交后同步固化到节点数据并幂等映射）
      if (node.code === 'MEITUAN_ORDER_REGISTER') {
        this.imageEditForm.meituanCode = this.scmCodeBaseline.meituanCode
        this.imageEditForm.shihengCode = this.scmCodeBaseline.shihengCode
      } else {
        this.imageEditForm.meituanCode = ''
        this.imageEditForm.shihengCode = ''
      }
      this.imageEditDialogVisible = true
    },

    // 图片编辑弹窗 - 图片预览
    handleImageEditPreview (imageUrl) {
      // ImageUpload 组件直接传递 imageUrl 参数
      if (imageUrl) {
        this.imagePreviewList = [imageUrl]
        this.currentImageUrl = imageUrl
        this.$nextTick(() => {
          this.$refs.imageViewer.clickHandler()
        })
      }
    },

    // 图片编辑弹窗 - 提交保存
    async confirmImageEdit () {
      // 从对象数组中提取 URL 字符串
      const imageUrls = this.imageEditForm.images.map(item => item.url)

      if (!imageUrls || imageUrls.length === 0) {
        return this.$message.warning('请至少上传一张图片')
      }

      // 美团订货注册：供应链编码处理（基准=财务映射表最新值，权威源；后端引擎同步兑底）
      const isScmNode = this.currentEditNode && this.currentEditNode.code === 'MEITUAN_ORDER_REGISTER'
      const scmM = isScmNode ? String(this.imageEditForm.meituanCode || '').trim() : ''
      const scmS = isScmNode ? String(this.imageEditForm.shihengCode || '').trim() : ''
      let scmEditable = false // 是否随 reupdate 提交编码字段
      if (isScmNode) {
        const base = this.scmCodeBaseline
        if (!scmM && !scmS) {
          if (base.meituanCode || base.shihengCode) {
            // 映射表现有编码被清空 = 想清码，拦截（督导端不清码，清码走财务）
            return this.$message.warning('请至少保留一个供应链编码（清空编码请联系财务在门店映射中处理）')
          }
          // 映射表无编码（存量项目/财务未维护）：允许仅编辑图片，不提交编码字段
        } else {
          scmEditable = true
        }
      }

      this.imageEditLoading = true
      try {
        const instanceId = this.currentDetail?.instance?._id
        const node = this.currentEditNode

        if (!instanceId || !node) {
          this.$message.error('数据不完整，无法保存')
          return
        }

        const submitData = {
          images: imageUrls,
          remark: this.imageEditForm.remark
        }
        // 美团订货注册：填了编码才随 reupdate 提交，后端联动同步供应链映射
        if (scmEditable) {
          submitData.meituanCode = scmM
          submitData.shihengCode = scmS
        }

        const { data: res } = await this.$http.post(`process/instances/${instanceId}/superExecute`, {
          nodeCode: node.code,
          action: 'reupdate',
          data: submitData
        })

        if (res.meta.status !== 200) {
          this.$message.error(res.meta.msg)
          return
        }

        this.$message.success('数据已更新')
        this.imageEditDialogVisible = false
        await this.loadInstanceDetail()
      } finally {
        this.imageEditLoading = false
      }
    },

    // 图片编辑弹窗关闭回调
    onImageEditDialogClosed () {
      this.currentEditNode = null
      this.imageEditForm.images = []
      this.imageEditForm.remark = ''
      this.imageEditForm.meituanCode = ''
      this.imageEditForm.shihengCode = ''
    },

    // ==================== 建店资料收集编辑功能 ====================

    // 打开 DATA_COLLECT 编辑弹窗
    handleEditDataCollect () {
      const node = this.dataCollectNode
      if (!node?.data) return

      // 将 images 转换为对象数组格式
      const images = node.data.images || []
      const normalizedImages = images.map(img => {
        if (typeof img === 'string') {
          return { url: img, hash: null }
        }
        return img
      })

      this.dataCollectEditForm = {
        storeAddress: node.data.storeAddress || '',
        legalPersonName: node.data.legalPersonName || '',
        legalPersonId: node.data.legalPersonId || '',
        bankPhone: node.data.bankPhone || '',
        bankName: node.data.bankName || '',
        bankAccount: node.data.bankAccount || '',
        storePhone: node.data.storePhone || '',
        lat: node.data.lat || null,
        lng: node.data.lng || null,
        province: node.data.province || '',
        city: node.data.city || '',
        district: node.data.district || '',
        remark: node.data.remark || '',
        images: normalizedImages
      }

      this.dataCollectEditDialogVisible = true
    },

    // DATA_COLLECT 编辑弹窗 - 自动定位
    async autoLocateEdit () {
      const address = this.dataCollectEditForm.storeAddress
      if (!address) {
        this.$message.warning('请先输入门店地址')
        return
      }
      const { data: res } = await this.$http.get('map/geocoder', { params: { address } })
      if (res.meta.status !== 200) return this.$message.error(res.meta.msg)

      this.dataCollectEditForm.lat = res.data.location.lat
      this.dataCollectEditForm.lng = res.data.location.lng
      this.dataCollectEditForm.province = res.data.address_components.province
      this.dataCollectEditForm.city = res.data.address_components.city
      this.dataCollectEditForm.district = res.data.address_components.district
      if (this.dataCollectEditMap) {
        this.dataCollectEditMap.setPoint(res.data.location.lat, res.data.location.lng)
      }
    },

    // DATA_COLLECT 编辑弹窗 - 地图初始化
    onDataCollectEditOpened () {
      this.$nextTick(() => {
        const lat = this.dataCollectEditForm.lat || 34.309615
        const lng = this.dataCollectEditForm.lng || 108.923302

        this.dataCollectEditMap = new ChoujianMap('data-collect-edit-map', lat, lng)
        if (this.dataCollectEditForm.lat && this.dataCollectEditForm.lng) {
          this.dataCollectEditMap.addPoint(lat, lng)
        }
        this.dataCollectEditMap.on('click', (lat, lng) => {
          this.dataCollectEditForm.lat = lat
          this.dataCollectEditForm.lng = lng
        })
      })
    },

    // DATA_COLLECT 编辑弹窗 - 提交保存
    async confirmDataCollectEdit () {
      const valid = await this.$refs.dataCollectEditFormRef?.validate().catch(() => false)
      if (!valid) return

      // 从对象数组中提取 URL 字符串
      const imageUrls = this.dataCollectEditForm.images.map(item => item.url)

      this.dataCollectEditLoading = true
      try {
        const instanceId = this.currentDetail?.instance?._id
        if (!instanceId) {
          this.$message.error('数据不完整，无法保存')
          return
        }

        // 该弹窗只服务"上级更正已完成节点"；建店专员补录地址定位走自己的
        // 建店工作台 → dataCollectAddress 专用接口
        const { data: res } = await this.$http.post(`process/instances/${instanceId}/superExecute`, {
          nodeCode: 'DATA_COLLECT',
          action: 'reupdate',
          data: {
            storeAddress: this.dataCollectEditForm.storeAddress,
            legalPersonName: this.dataCollectEditForm.legalPersonName,
            legalPersonId: this.dataCollectEditForm.legalPersonId,
            bankPhone: this.dataCollectEditForm.bankPhone,
            bankName: this.dataCollectEditForm.bankName,
            bankAccount: this.dataCollectEditForm.bankAccount,
            storePhone: this.dataCollectEditForm.storePhone,
            lat: this.dataCollectEditForm.lat,
            lng: this.dataCollectEditForm.lng,
            province: this.dataCollectEditForm.province,
            city: this.dataCollectEditForm.city,
            district: this.dataCollectEditForm.district,
            images: imageUrls,
            remark: this.dataCollectEditForm.remark
          }
        })

        if (res.meta.status !== 200) {
          this.$message.error(res.meta.msg)
          return
        }

        this.$message.success('资料已更新')
        this.dataCollectEditDialogVisible = false
        await this.loadInstanceDetail()
      } catch (error) {
        this.$message.error('操作失败，请稍后重试')
      } finally {
        this.dataCollectEditLoading = false
      }
    },

    // DATA_COLLECT 编辑弹窗关闭回调
    onDataCollectEditClosed () {
      if (this.dataCollectEditMap) {
        this.dataCollectEditMap.removeAllListeners('click')
        this.dataCollectEditMap.destroy()
        this.dataCollectEditMap = null
      }
      this.dataCollectEditForm = {}
    },

    // ==================== 数据编辑功能（人员重新选择 / 日期修改） ====================

    // 打开数据编辑弹窗
    async handleDataEditNode (node) {
      this.currentDataEditNode = node

      // 一次性初始化所有属性，确保 Vue 2 响应性
      if (node.employeeSelection) {
        this.dataEditForm = {
          employeeId: node.data?.employeeId || '',
          employeeName: node.data?.employeeName || '',
          scheduledAt: node.data?.scheduledAt ? new Date(node.data.scheduledAt) : ''
        }
        // 加载员工列表
        try {
          const { data: res } = await this.$http.get('process/getEmployeeList', {
            params: { role: node.employeeSelection.role }
          })
          if (res.meta.status === 200) {
            this.dataEditEmployeeList = res.data || []
          }
        } catch (error) {
          console.error('获取员工列表失败:', error)
          this.dataEditEmployeeList = []
        }
      } else if (node.code === 'STORE_OPENING') {
        this.dataEditForm = {
          openingAt: node.data?.openingAt ? new Date(node.data.openingAt) : ''
        }
      } else {
        this.dataEditForm = {}
      }

      this.dataEditDialogVisible = true
    },

    // 数据编辑弹窗 — 员工选择变更
    onDataEditEmployeeChange (employeeId) {
      const emp = this.dataEditEmployeeList.find(e => e._id === employeeId)
      this.dataEditForm.employeeId = employeeId
      this.dataEditForm.employeeName = emp?.name || ''
    },

    // 数据编辑弹窗 — 提交保存
    async confirmDataEdit () {
      const node = this.currentDataEditNode
      if (!node) return

      // 前端校验
      if (node.employeeSelection && !this.dataEditForm.employeeId) {
        return this.$message.warning('请选择人员')
      }
      if (node.code === 'STORE_OPENING' && !this.dataEditForm.openingAt) {
        return this.$message.warning('请选择开业时间')
      }

      this.dataEditLoading = true
      try {
        const instanceId = this.currentDetail?.instance?._id
        const { data: res } = await this.$http.post(`process/instances/${instanceId}/superExecute`, {
          nodeCode: node.code,
          action: 'reupdate',
          data: { ...this.dataEditForm }
        })

        if (res.meta.status !== 200) {
          this.$message.error(res.meta.msg)
          return
        }

        this.$message.success('数据已更新')
        this.dataEditDialogVisible = false
        await this.loadInstanceDetail()
      } catch (error) {
        this.$message.error('操作失败，请稍后重试')
      } finally {
        this.dataEditLoading = false
      }
    },

    // 数据编辑弹窗关闭回调
    onDataEditDialogClosed () {
      this.currentDataEditNode = null
      this.dataEditForm = {}
      this.dataEditEmployeeList = []
    },

    // ==================== 产品审核记录编辑 ====================

    // 打开产品审核记录编辑弹窗
    handleEditProductReview (record, index) {
      this.currentEditProductReview = {
        instanceId: this.currentDetail?.instance?._id,
        recordIndex: index,
        record
      }
      // 将 URL 字符串数组转换为对象数组格式
      const images = record.images || []
      this.productReviewEditForm.images = images.map(img => {
        if (typeof img === 'string') {
          return { url: img, hash: null }
        }
        return img
      })
      this.productReviewEditForm.remark = record.remark || ''
      this.productReviewEditDialogVisible = true
    },

    // 产品审核编辑 - 图片预览
    handleProductReviewEditPreview (imageUrl) {
      // ImageUpload 组件直接传递 imageUrl 参数
      if (imageUrl) {
        this.imagePreviewList = [imageUrl]
        this.currentImageUrl = imageUrl
        this.$nextTick(() => {
          this.$refs.imageViewer.clickHandler()
        })
      }
    },

    // 产品审核编辑 - 提交保存
    async confirmProductReviewEdit () {
      // 从对象数组中提取 URL 字符串
      const imageUrls = this.productReviewEditForm.images.map(item => item.url)

      if (!imageUrls || imageUrls.length === 0) {
        return this.$message.warning('请至少上传一张图片')
      }

      this.productReviewEditLoading = true
      try {
        const { instanceId, recordIndex } = this.currentEditProductReview

        if (!instanceId) {
          this.$message.error('数据不完整，无法保存')
          return
        }

        const node = this.currentDetail?.nodes?.find(n => n.code === 'PRODUCT_REVIEW')
        if (!node) {
          this.$message.error('找不到产品审核节点')
          return
        }

        const updatedLoopRecords = [...(node.loopRecords || [])]
        if (updatedLoopRecords[recordIndex]) {
          updatedLoopRecords[recordIndex] = {
            ...updatedLoopRecords[recordIndex],
            images: imageUrls,
            remark: this.productReviewEditForm.remark
          }
        }

        const { data: res } = await this.$http.post(`process/instances/${instanceId}/superExecute`, {
          nodeCode: 'PRODUCT_REVIEW',
          action: 'reupdate',
          data: {
            loopRecords: updatedLoopRecords
          }
        })

        if (res.meta.status !== 200) {
          this.$message.error(res.meta.msg)
          return
        }

        this.$message.success('产品审核记录已更新')
        this.productReviewEditDialogVisible = false
        await this.loadInstanceDetail()
      } finally {
        this.productReviewEditLoading = false
      }
    },

    // 产品审核编辑弹窗关闭回调
    onProductReviewEditDialogClosed () {
      this.currentEditProductReview = null
      this.productReviewEditForm.images = []
      this.productReviewEditForm.remark = ''
    }
  }
}
</script>

<style lang="less" scoped>
// 法人检测提示样式
.legal-check-tip {
  margin-bottom: 15px;
}

// 详情页容器
.detail-page-container {
  padding: 0 20px;
}

// ===== 新详情页样式 =====
// 顶部项目信息区
.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 20px;
  border-bottom: 1px solid #E8E8E8;
  // margin-bottom: 14px;
}

// 首批物料备忘警示卡片
.detail-memo-card {
  background: #FDF6EC;
  border-left: 3px solid #E6A23C;
  border-radius: 4px;
  padding: 12px 14px;
  margin-top: 20px;
}
.detail-memo-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.detail-memo-title {
  font-weight: bold;
  color: #B88230;
  font-size: 14px;

  i {
    margin-right: 4px;
  }
}
.detail-memo-source {
  font-size: 12px;
  color: #909399;
}
.detail-memo-text {
  font-size: 13px;
  color: #606266;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-all;
}
.detail-memo-images {
  margin-top: 8px;
}

.detail-header-left {
  flex: 1;
}

.detail-project-name {
  font-size: 20px;
  font-weight: bold;
  color: #0D0D0D;
  margin-bottom: 8px;

  i {
    margin-right: 8px;
    color: #409eff;
  }
}

.detail-meta-row {
  display: flex;
  gap: 24px;
}

.detail-meta-item {
  font-size: 13px;
}

.detail-meta-label {
  color: #7A7A7A;
}

.detail-meta-value {
  color: #0D0D0D;
  font-weight: 500;
}

.detail-address {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 13px;

  i {
    color: #F56C6C;
    font-size: 16px;
    flex-shrink: 0;
  }
}

.detail-address-text {
  color: #606266;
  line-height: 1.4;
}

.detail-refresh-icon {
  cursor: pointer;
  color: #909399;
  transition: all 0.3s;
  margin-left: 8px;
  font-size: 14px;

  &:hover {
    color: #409EFF;
    transform: rotate(180deg);
  }
}

.detail-progress-card {
  width: 180px;
  padding: 16px;
  border: 1px solid #E8E8E8;
  border-radius: 4px;
  text-align: center;
}

.detail-progress-label {
  font-size: 12px;
  color: #7A7A7A;
  margin-bottom: 8px;
}

.detail-progress-value {
  margin-bottom: 8px;
}

.detail-progress-number {
  font-size: 36px;
  font-weight: 600;
  color: #0D0D0D;
}

.detail-progress-unit {
  font-size: 18px;
  color: #7A7A7A;
}

.detail-progress-info {
  font-size: 12px;
  color: #B0B0B0;
  margin-top: 8px;
}

// 阶段进度区域
.detail-section {
  margin-bottom: 24px;
}

.detail-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.detail-section-title {
  font-size: 16px;
  font-weight: bold;
  color: #0D0D0D;
  padding: 10px 0;
  padding-top:15px;
}

.detail-section-toggle {
  display: flex;
  align-items: center;
}

.stage-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.stage-card {
  width: 20%;
  padding: 12px;
  border: 1px solid #E8E8E8;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
}

.stage-name {
  font-size: 13px;
  font-weight: 500;
  color: #0D0D0D;
  margin-bottom: 4px;
}

.stage-status {
  font-size: 12px;
  margin-bottom: 8px;
}

.stage-pending {
  .stage-status { color: #B0B0B0; }
}

.stage-processing {
  .stage-status { color: #E42313; }
}

.stage-complete {
  .stage-status { color: #22C55E; }
}

// 底部操作区域
.detail-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0;
  border-top: 1px solid #E8E8E8;
  margin-top: 20px;
}

.detail-actions-left {
  display: flex;
  align-items: center;
  gap: 4px;
}

.detail-actions-label {
  font-size: 13px;
  color: #7A7A7A;
}

.detail-actions-node {
  font-size: 13px;
  font-weight: 500;
  color: #E42313;
}

.detail-actions-right {
  display: flex;
  gap: 12px;
}

// 文字辅助类
.text-muted {
  color: #B0B0B0;
}

.node-days-tag {
  font-size: 12px;
  color: #F56C6C;
  font-weight: normal;
  margin-left: 6px;
}

.text-waiting {
  color: #E6A23C;
  font-size: 12px;
}

// 节点详情弹窗
.node-detail-content {
  .node-detail-assignee {
    margin-top: 16px;
  }
  .node-detail-section-title {
    font-size: 14px;
    font-weight: 500;
    color: #303133;
    margin-bottom: 12px;
  }
  .node-detail-data {
    margin-top: 16px;
  }
  .node-detail-item {
    margin-bottom: 12px;
  }
  .node-detail-label {
    font-size: 13px;
    color: #909399;
    margin-bottom: 4px;
  }
  .node-detail-value {
    font-size: 14px;
    color: #303133;
  }
}

// 地图信息显示
.map-info {
  font-size: 12px;
  color: #666;
  margin-bottom: 10px;
  padding: 8px;
  background: #f5f7fa;
  border-radius: 4px;
}

// 地图容器
.map-container {
  width: 100%;
  height: 400px;
}

// 循环节点信息
.loop-info {
  color: #909399;
  font-size: 13px;
  margin-right: 20px;
}

/* FEEDBACK 审核内容展示区域 */
.feedback-review-content {
  margin-bottom: 20px;
  padding: 16px;
  background-color: #f8f9fc;
  border-radius: 8px;
  border: 1px solid #e8eaef;
  max-height: 50vh;
  overflow-y: auto;
}

.feedback-review-header {
  display: flex;
  align-items: center;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 12px;
}

.feedback-review-header i {
  margin-right: 8px;
  color: #409eff;
  font-size: 18px;
}

/* 历史审核记录 */
.feedback-history {
  margin-bottom: 16px;
}

.feedback-history-title {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #909399;
  margin-bottom: 8px;
}

.feedback-history-title i {
  margin-right: 6px;
}

.feedback-history-item {
  padding: 10px 12px;
  margin-bottom: 8px;
  background-color: #fff;
  border-radius: 6px;
  border: 1px solid #ebeef5;
}

.feedback-history-meta {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #909399;
  margin-bottom: 6px;
}

.feedback-history-images {
  margin-bottom: 6px;
}

.feedback-history-opinion {
  font-size: 13px;
  color: #e6a23c;
}

.feedback-history-time {
  font-size: 12px;
}

/* 当前提交内容 */
.feedback-current-submission {
  padding: 0;
}

.feedback-current-title {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  color: #606266;
  margin-bottom: 10px;
}

.feedback-current-title i {
  margin-right: 6px;
  color: #67c23a;
}

.feedback-images {
  display: flex;
  flex-wrap: wrap;
}

.feedback-no-images {
  color: #c0c4cc;
  font-size: 13px;
  padding: 10px 0;
}

.feedback-remark {
  margin-top: 8px;
  font-size: 13px;
  color: #606266;
}

.feedback-label {
  font-weight: 500;
  color: #909399;
}

/* ===== 实操培训答题结果展示区 ===== */
.exam-result-section {
  margin-bottom: 10px;
}

.exam-result-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #606266;
  margin-bottom: 12px;
}

.exam-result-header i {
  font-size: 16px;
  color: #409EFF;
}

.exam-result-empty {
  color: #909399;
  font-size: 13px;
  padding: 10px 0;
}

/* ===== 图片编辑弹窗样式 ===== */
.image-edit-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* 美团订货注册：供应链编码录入提示（节点表单与图片编辑弹窗共用） */
.scm-code-tip {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
  margin-top: 2px;
}

.image-edit-node-name {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.image-edit-status {
  display: flex;
  align-items: center;
}

.image-edit-time {
  font-size: 13px;
  color: #909399;
  margin-left: auto;
}
</style>

<style lang="less">
// 上传图片的样式（全局覆盖）
.upload-class {
  .el-upload--picture-card{
    width: 100px;
    height: 100px;
    line-height: 100px;
  }
  .el-upload-list--picture-card .el-upload-list__item{
    width: 100px;
    height: 100px;
  }
}
</style>
